import { api } from "@/lib/api"
import {
  generateRecoveryMnemonic,
  recoveryKeyFromMnemonic,
  unwrapKeyFromRecovery,
  wrapDekForNewPin,
  wrapKeyForRecovery,
} from "@/lib/crypto"

// Household private mode (docs/plans/household-private-mode.md): one DEK per household. A member
// shares it by wrapping it under a one-time 12-word code that travels only in the link's URL
// fragment (#hk=…), which browsers never send to the server. The server holds the wrapped blob
// for the one member it's addressed to; without the code it can't unwrap it.

const HASH_PREFIX = "#hk="

/** Shares the household DEK (must be extractable: unwrap it from the PIN first) with a member. */
export async function createKeyShareLink(dek: CryptoKey, forUserId: string): Promise<string> {
  const code = generateRecoveryMnemonic()
  const wrappedDek = await wrapKeyForRecovery(dek, await recoveryKeyFromMnemonic(code))
  const { id } = await api.post<{ id: string }>("/auth-crypto/grants", { forUserId, wrappedDek })
  return `${window.location.origin}/${HASH_PREFIX}${id}.${code.split(" ").join("-")}`
}

/** The share carried in a URL hash, or null. */
export function readKeyShare(hash: string): { grantId: string; code: string } | null {
  if (!hash.startsWith(HASH_PREFIX)) return null
  const [grantId, words] = hash.slice(HASH_PREFIX.length).split(".")
  if (!grantId || !words) return null
  return { grantId, code: words.split("-").join(" ") }
}

/**
 * Unwraps the shared DEK with the code, re-wraps it under this member's new PIN and a fresh
 * recovery phrase, and turns private mode on for them. Returns the DEK and the phrase to show.
 */
export async function claimKeyShare(
  share: { grantId: string; code: string },
  pin: string,
): Promise<{ dek: CryptoKey; phrase: string }> {
  const { wrappedDek: blob } = await api.get<{ wrappedDek: string }>(
    `/auth-crypto/grants/${share.grantId}`,
  )
  let shared: CryptoKey
  try {
    shared = await unwrapKeyFromRecovery(blob, await recoveryKeyFromMnemonic(share.code))
  } catch {
    throw new Error("This key link is damaged. Ask for a new one.")
  }
  const { dek, kdfSalt, wrappedDek } = await wrapDekForNewPin(pin, shared)
  const phrase = generateRecoveryMnemonic()
  const encryptedRecoveryKey = await wrapKeyForRecovery(dek, await recoveryKeyFromMnemonic(phrase))
  await api.post(`/auth-crypto/grants/${share.grantId}/claim`, {
    kdfSalt,
    wrappedDek,
    encryptedRecoveryKey,
  })
  return { dek, phrase }
}
